import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

export const brunPrices = (current, brun) => {
  const saving = Math.floor(brun * 15 / 100);
  return { saving, discounted: brun - saving, total: current + brun - saving };
};

class FlexoraBrunBundle extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
    this.active = this.dataset.offerEnabled === 'true';
    this.visibleOffer = this.dataset.bundleVisible === 'true';
    this.selected = this.config.product.variants.find(v => String(v.id) === String(this.config.selectedVariantId));
    this.button = this.querySelector('[data-brun-bundle-button]');
    this.status = this.querySelector('.flexora-winter-card__status');
    this.root = window.Shopify?.routes?.root || '/';
    this.section = this.closest('.shopify-section') || document.querySelector('main');
    this.busy = false;
    const options = { signal: this.abort.signal };
    this.button.addEventListener('click', event => this.add(event), options);
    this.section?.addEventListener(StandardEvents.productSelect, async event => {
      if (String(event.detail?.product?.id) !== String(this.config.product.id)) return;
      const generation = this.variantGeneration = (this.variantGeneration || 0) + 1;
      this.variantPending = true;
      this.paint();
      try {
        const result = await event.detail.promise;
        if (!this.isConnected || generation !== this.variantGeneration) return;
        const id = result?.variant?.id || result?.detail?.resource?.id;
        this.selected = this.config.product.variants.find(v => String(v.id) === String(id));
      } catch { if (generation !== this.variantGeneration) return; this.selected = null; }
      this.variantPending = false;
      this.paint();
    }, options);
    this.section?.addEventListener('change', event => {
      if (!event.target.closest('product-form-component,variant-picker')) return;
      queueMicrotask(() => this.syncForm());
    }, options);
    this.observer = new MutationObserver(() => this.syncForm());
    this.section?.querySelectorAll('product-form-component input[name="id"]').forEach(input => this.observer.observe(input, { attributes: true, attributeFilter: ['value','disabled'] }));
    window.addEventListener('pageshow', () => this.refreshPrices().catch(() => {}), options);
    window.addEventListener('focus', () => this.refreshPrices().catch(() => {}), options);
    this.motionObserver?.disconnect();
    if (this.visibleOffer && 'IntersectionObserver' in window) {
      this.motionObserver = new IntersectionObserver(entries => {
        this.dataset.motionVisible = String(entries[0].isIntersecting);
      }, { threshold: .15 });
      this.motionObserver.observe(this.querySelector('.flexora-winter-card__pair') || this);
    }
    this.paint();
  }

  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.motionObserver?.disconnect(); this.flights?.forEach(animation => animation.cancel()); }

  syncForm() {
    if (this.variantPending || this.busy) return;
    const form = [...(this.section?.querySelectorAll('product-form-component') || [])].find(form => String(form.dataset.productId) === String(this.config.product.id));
    const input = form?.querySelector('input[name="id"]');
    if (input) {
      this.selected = input.disabled ? null : this.config.product.variants.find(v => String(v.id) === String(input.value));
      this.paint();
    }
  }

  money(cents, code = false) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: this.config.currency || 'CAD', currencyDisplay: code ? 'code' : 'symbol' }).format(cents / 100);
  }

  paint() {
    if (this.pricingMismatch) { this.button.disabled = true; this.button.textContent = 'Review your cart'; return; }
    const available = !!this.selected?.available && !!this.config.brunVariant?.available && !this.variantPending;
    this.button.disabled = this.busy || (this.visibleOffer && (!this.active || !available));
    if (!this.visibleOffer) this.button.disabled = this.busy || !this.config.brunVariant?.available;
    if (this.busy) return;
    this.button.textContent = this.visibleOffer ? (this.variantPending ? 'Updating…' : available ? 'Add Both & Save 15%' : 'Choose an available variant') : '+ Add Liquid Brun';
    if (!this.visibleOffer) { this.querySelector('[data-brun-original]').textContent = this.money(this.config.brunVariant.price); return; }
    const prices = brunPrices(this.selected?.price || 0, this.config.brunVariant.price);
    this.querySelector('[data-brun-original]').textContent = this.money(this.config.brunVariant.price);
    this.querySelector('[data-brun-discounted]').textContent = this.money(prices.discounted);
    this.querySelector('[data-bundle-total]').textContent = this.selected ? 'Get Both for ' + this.money(prices.total, true) : 'Choose an available variant';
  }

  async json(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || 'Could not update your cart. Please try again.');
    return result;
  }

  async refreshPrices() {
    if (this.busy) return;
    const selectedId = this.selected?.id;
    const [current, brun] = await Promise.all([
      this.json(this.dataset.productUrl + '.js'),
      this.json(this.dataset.brunUrl + '.js')
    ]);
    if (!this.isConnected) return;
    this.config.product = current;
    this.config.brunVariant = brun.variants.find(v => String(v.id) === String(this.config.brunVariant.id)) || brun.variants.find(v => v.available) || brun.variants[0];
    this.selected = current.variants.find(v => String(v.id) === String(this.selected?.id || selectedId));
    this.paint();
  }


  async flyBundleToCart(cart, items) {
    if (!this.active || !this.isConnected || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const destination = [...document.querySelectorAll('.header-actions__cart-icon')].find(icon => {
      const rect = icon.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });
    if (!destination || typeof Element.prototype.animate !== 'function') return;
    const origin = this.button.getBoundingClientRect();
    const target = destination.getBoundingClientRect();
    this.flights ||= new Set();
    await Promise.allSettled(items.map(async (item, index) => {
      const line = cart.items.find(line => String(line.variant_id) === String(item.id));
      if (!line?.image) return;
      const image = document.createElement('img');
      image.src = line.image;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.className = 'flexora-brun-cart-flight';
      const startX = Math.max(38, Math.min(window.innerWidth - 38, origin.left + origin.width / 2 + (index ? 22 : -22)));
      const startY = Math.max(50, Math.min(window.innerHeight - 50, origin.top + origin.height / 2));
      const dx = target.left + target.width / 2 - startX;
      const dy = target.top + target.height / 2 - startY;
      Object.assign(image.style, {
        position: 'fixed', left: startX + 'px', top: startY + 'px',
        width: '72px', height: '88px', objectFit: 'contain',
        background: '#faf6ee', borderRadius: '10px', pointerEvents: 'none',
        zIndex: '2147483647', boxShadow: '0 6px 20px #0003'
      });
      document.body.appendChild(image);
      let animation;
      try {
        animation = image.animate([
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          { transform: 'translate(calc(-50% + ' + dx * .45 + 'px), calc(-50% + ' + (dy * .5 - 50) + 'px)) scale(.7)', opacity: 1, offset: .5 },
          { transform: 'translate(calc(-50% + ' + dx + 'px), calc(-50% + ' + dy + 'px)) scale(.12)', opacity: 0 }
        ], { duration: 780, delay: index * 100, easing: 'cubic-bezier(.25,.65,.35,1)', fill: 'both' });
        this.flights.add(animation);
        await animation.finished;
      } finally {
        this.flights.delete(animation);
        image.remove();
      }
    }));
  }

  async add(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.busy || this.button.disabled) return;
    this.syncForm();
    if (this.button.disabled) return;
    const selectedId = this.selected?.id;
    this.busy = true;
    this.button.disabled = true;
    this.button.textContent = 'Checking prices…';
    this.status.textContent = '';
    let added = false;
    try {
      const [current, brun, before] = await Promise.all([
        this.json(this.dataset.productUrl + '.js'),
        this.json(this.dataset.brunUrl + '.js'),
        this.json(this.root + 'cart.js')
      ]);
      const selected = current.variants.find(v => String(v.id) === String(selectedId));
      const brunVariant = brun.variants.find(v => String(v.id) === String(this.config.brunVariant.id));
      if (!brunVariant?.available || (this.active && !selected?.available)) throw new Error('This combination is currently unavailable. Choose an available variant.');
      if (this.active && before.items.some(item => String(item.product_id) === String(brun.id))) throw new Error('Liquid Brun is already in your cart. Review your cart before adding another offer.');
      this.config.product = current;
      this.config.brunVariant = brunVariant;
      this.config.currency = before.currency;
      this.selected = selected;
      const items = this.active ? [{ id: selected.id, quantity: 1 }, { id: brunVariant.id, quantity: 1 }] : [{ id: brunVariant.id, quantity: 1 }];
      this.button.textContent = 'Adding…';
      await this.json(this.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      added = true;
      const cart = await this.json(this.root + 'cart.js');
      const quantity = (c, id) => c.items.filter(i => String(i.variant_id) === String(id)).reduce((n,i) => n + i.quantity, 0);
      if (items.some(i => quantity(cart,i.id) < quantity(before,i.id) + 1)) throw new Error('Please review your cart to confirm both items were added.');
      await this.flyBundleToCart(cart, items).catch(() => {});
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'flexora-winter-card' } }));
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map(i => ({ merchandiseId: String(i.id), quantity: i.quantity })), promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'flexora-brun-bundle' } }) }));
      if (this.active) {
        const expected = brunPrices(selected.price,brunVariant.price);
        const brunLine = cart.items.find(i => String(i.variant_id) === String(brunVariant.id));
        const mainLines = cart.items.filter(i => String(i.variant_id) === String(selected.id));
        const exact = brunLine?.quantity === 1 && brunLine.final_price === expected.discounted && mainLines.every(i => i.final_price === selected.price) && !cart.cart_level_discount_applications?.length;
        if (!exact) {
          this.pricingMismatch = true;
          this.querySelector('.flexora-winter-card__bundle-summary').hidden = true;
          this.querySelector('[data-brun-discounted]').hidden = true;
          this.querySelector('[data-brun-original]').textContent = this.money(brunLine?.final_price || brunVariant.price);
          this.querySelector('[data-brun-original]').style.textDecoration = 'none';
          this.status.textContent = 'Items added. Shopify applied different pricing or another offer. Actual cart subtotal: ' + this.money(cart.total_price, true) + '. Review your cart before checkout.';
        } else this.status.textContent = 'Both fragrances added! Liquid Brun: ' + this.money(brunLine.final_price) + '. Cart subtotal: ' + this.money(cart.total_price, true) + ' before shipping.';
      } else this.status.textContent = 'Liquid Brun added to your cart!';
      const link = document.createElement('a');
      link.href = this.root + 'cart';
      link.textContent = ' View your cart';
      this.status.appendChild(link);
    } catch (error) { this.status.textContent = (added ? 'Items may already be in your cart. ' : '') + error.message; }
    finally { this.busy = false; this.paint(); }
  }
}

if (!customElements.get('flexora-brun-bundle')) customElements.define('flexora-brun-bundle', FlexoraBrunBundle);
